/**
 * AutoDetectLocationBanner — first-visit banner that offers to set the
 * user's Social Pulse locality from their IP-detected geo.
 *
 * Behaviour:
 * - Only renders when the user has NOT set a locality yet (status !== 'ready').
 * - Only renders when `useGeo` produced a country + city (so the offer is
 *   specific — "Portland, OR, US" not "somewhere on Earth").
 * - Once dismissed (either Accept or Decline) it hides for 7 days via
 *   localStorage.
 */
import { useEffect, useMemo, useState } from "react";
import { MapPin, X, Check } from "lucide-react";
import { useGeo } from "@/lib/useGeo";
import { useLocality } from "@/contexts/LocalityContext";

const LS_KEY = "sp_autodetect_dismissed_v1";
const DISMISS_DAYS = 7;

const isDismissed = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!ts) return false;
    return (Date.now() - ts) < (DISMISS_DAYS * 24 * 60 * 60 * 1000);
  } catch { return false; }
};

const markDismissed = () => {
  try { localStorage.setItem(LS_KEY, String(Date.now())); } catch {}
};

export default function AutoDetectLocationBanner() {
  const { geo } = useGeo();
  const { locality, status, save } = useLocality();
  const [hidden, setHidden] = useState(() => isDismissed());
  const [cookieOk, setCookieOk] = useState(() => {
    try { return Boolean(localStorage.getItem("wofy_cookie_consent")); } catch { return true; }
  });

  // Once locality is set we never want to nag again.
  useEffect(() => {
    if (locality?.city && locality?.country) setHidden(true);
  }, [locality?.city, locality?.country]);

  // Wait for cookie consent to be decided before offering the banner —
  // otherwise the fixed bottom-right cookie modal overlaps our accept
  // button on md+ viewports.
  useEffect(() => {
    if (cookieOk) return;
    const handler = () => setCookieOk(true);
    window.addEventListener("wofy-cookie-consent", handler);
    // Also poll for the localStorage key (in case another tab writes it).
    const iv = setInterval(() => {
      try {
        if (localStorage.getItem("wofy_cookie_consent")) { setCookieOk(true); clearInterval(iv); }
      } catch {}
    }, 1000);
    return () => { window.removeEventListener("wofy-cookie-consent", handler); clearInterval(iv); };
  }, [cookieOk]);

  const detected = useMemo(() => {
    if (!geo?.city || !geo?.country_name) return null;
    return {
      city: geo.city,
      state: geo.region || "",
      country: geo.country_name,
      country_code: geo.country_code || "",
      lat: geo.lat,
      lng: geo.lng,
    };
  }, [geo]);

  if (hidden) return null;
  if (!cookieOk) return null;               // wait for cookie consent decision
  if (status === "ready") return null;    // user already has a locality
  if (!detected) return null;              // geo not resolved yet

  const accept = async () => {
    setHidden(true);
    markDismissed();
    await save(detected);
  };
  const decline = () => {
    setHidden(true);
    markDismissed();
  };

  const label = [detected.city, detected.state, detected.country].filter(Boolean).join(", ");

  return (
    <div
      data-testid="auto-detect-location-banner"
      className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-4"
    >
      <div
        className="rounded-2xl border px-4 md:px-6 py-3 md:py-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-4"
        style={{
          borderColor: "var(--wofy-primary)",
          background: "rgba(178, 93, 56, 0.06)",
        }}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div
            className="w-9 h-9 rounded-full grid place-items-center shrink-0 text-white"
            style={{ background: "var(--wofy-primary)" }}
            aria-hidden
          >
            <MapPin className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-wider opacity-70">
              We detected your area
            </div>
            <div className="font-medium text-sm md:text-base">
              You’re near <span className="font-semibold">{label}</span>. Set this as your Social Pulse location to see local sports, education, government, news &amp; causes?
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 self-start md:self-auto">
          <button
            onClick={accept}
            data-testid="auto-detect-accept"
            className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full text-white hover:shadow-md transition"
            style={{ background: "var(--wofy-primary)" }}
          >
            <Check className="w-4 h-4" /> Yes, set my location
          </button>
          <button
            onClick={decline}
            data-testid="auto-detect-dismiss"
            aria-label="Dismiss"
            className="w-9 h-9 grid place-items-center rounded-full border hover:bg-black/5 transition"
            style={{ borderColor: "var(--wofy-border)" }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
